import { useState } from 'react';
import type { CustomerType } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { CustomerTypeFormModal } from './CustomerTypeFormModal.js';
import { useCustomerTypes } from './api.js';

export function CustomerTypesPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerType | null>(null);

  const query = useCustomerTypes({ page, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'sortOrder' });
  const types = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (customerType: CustomerType): void => {
    setEditing(customerType);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Customer Types"
        description="Configure the global, ordered list of customer types."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="customers" className="h-4 w-4" />
              New type
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={types.length === 0}
              emptyTitle="No customer types found"
              emptyDescription="Create your first customer type to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New type</Button> : undefined}
            >
              {types.map((customerType) => (
                <tr
                  key={customerType.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(customerType)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">
                    {customerType.sortOrder}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{customerType.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={customerType.isActive ? 'green' : 'gray'}>
                      {customerType.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && types.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <CustomerTypeFormModal
        isOpen={formOpen}
        customerType={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
